import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import { downloadBlob } from '@/utils/download';
import { DEFAULT_BILLING_PAGE_SIZE } from '../constants/billing.constants';
import {
  allCostResponseSchema,
  dcrEntryDetailSchema,
  expenseApprovalDetailSchema,
  expenseHeaderResponseSchema,
  expenseTypeListSchema,
  offlineDcrResponseSchema,
  offlineDerResponseSchema,
  transportTypeListSchema,
  type AllCostResponse,
  type AllCostSearch,
  type AmendDcrPayload,
  type AmendDerPayload,
  type ApproveDcrPayload,
  type ApproveDerPayload,
  type ApproveExpensePayload,
  type DcrEntryDetail,
  type ExpenseApprovalDetail,
  type ExpenseApprovalSearch,
  type ExpenseHeaderResponse,
  type CreateWebExpensePayload,
  type DcrApprovalParams,
  type DerApprovalParams,
  type ExpenseType,
  type OfflineDcrResponse,
  type OfflineDcrSearch,
  type OfflineDerResponse,
  type OfflineDerSearch,
  type TransportType,
  type VerifyDcrPayload,
  type VerifyDerPayload,
} from '../types/billing.types';

export const billingQueryKeys = {
  all: () => ['billing-verification'] as const,
  offlineDcr: (params: OfflineDcrSearch) =>
    [...billingQueryKeys.all(), 'offline-dcr', params] as const,
  offlineDer: (params: OfflineDerSearch) =>
    [...billingQueryKeys.all(), 'offline-der', params] as const,
  transportTypes: () => [...billingQueryKeys.all(), 'transport-types'] as const,
  dcrEntry: (recordId: number) => [...billingQueryKeys.all(), 'dcr-entry', recordId] as const,
  expenseTypes: () => [...billingQueryKeys.all(), 'expense-types'] as const,
  allCost: (params: AllCostSearch) => [...billingQueryKeys.all(), 'all-cost', params] as const,
  dcrApproval: (params: DcrApprovalParams) =>
    [...billingQueryKeys.all(), 'dcr-approval', params] as const,
  derApproval: (params: DerApprovalParams) =>
    [...billingQueryKeys.all(), 'der-approval', params] as const,
  expenseApprovals: (params: ExpenseApprovalSearch) =>
    [...billingQueryKeys.all(), 'expense-approval', params] as const,
  expenseApproval: (expenseId: number) =>
    [...billingQueryKeys.all(), 'expense-approval', 'detail', expenseId] as const,
};

export const offlineDcrInfiniteQueryOptions = (params: OfflineDcrSearch) =>
  infiniteQueryOptions({
    queryKey: billingQueryKeys.offlineDcr(params),
    queryFn: async ({ pageParam }): Promise<OfflineDcrResponse> => {
      const res = await apiClient.get(API.billing.offlineDcrVerify, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_BILLING_PAGE_SIZE },
      });
      return offlineDcrResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const offlineDerInfiniteQueryOptions = (params: OfflineDerSearch) =>
  infiniteQueryOptions({
    queryKey: billingQueryKeys.offlineDer(params),
    queryFn: async ({ pageParam }): Promise<OfflineDerResponse> => {
      const res = await apiClient.get(API.billing.offlineDerVerify, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_BILLING_PAGE_SIZE },
      });
      return offlineDerResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const dcrEntryQueryOptions = (recordId: number | null) =>
  queryOptions({
    queryKey: billingQueryKeys.dcrEntry(recordId ?? 0),
    queryFn: async (): Promise<DcrEntryDetail> => {
      const res = await apiClient.get(API.billing.dcrEntry(recordId!));
      return dcrEntryDetailSchema.parse(res.data);
    },
    enabled: recordId !== null,
    staleTime: STALE_TIME,
  });

export const transportTypeQueryOptions = () =>
  queryOptions({
    queryKey: billingQueryKeys.transportTypes(),
    queryFn: async (): Promise<TransportType[]> => {
      const res = await apiClient.get(API.billing.transportTypes);
      return transportTypeListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export async function verifyDcr(recordId: number, payload: VerifyDcrPayload): Promise<void> {
  await apiClient.patch(API.billing.verifyDcr(recordId), payload);
}

export async function verifyDer(detailId: number, payload: VerifyDerPayload): Promise<void> {
  await apiClient.patch(API.billing.verifyDer(detailId), payload);
}

export async function downloadDerAttachment(detailId: number, fileName: string): Promise<void> {
  const res = await apiClient.get(API.billing.derAttachment(detailId), {
    responseType: 'blob',
  });
  downloadBlob(res.data as Blob, fileName);
}

export const dcrApprovalInfiniteQueryOptions = (params: DcrApprovalParams) =>
  infiniteQueryOptions({
    queryKey: billingQueryKeys.dcrApproval(params),
    queryFn: async ({ pageParam }): Promise<OfflineDcrResponse> => {
      const res = await apiClient.get(API.billing.dcrApproval, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_BILLING_PAGE_SIZE },
      });
      return offlineDcrResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const derApprovalInfiniteQueryOptions = (params: DerApprovalParams) =>
  infiniteQueryOptions({
    queryKey: billingQueryKeys.derApproval(params),
    queryFn: async ({ pageParam }): Promise<OfflineDerResponse> => {
      const res = await apiClient.get(API.billing.derApproval, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_BILLING_PAGE_SIZE },
      });
      return offlineDerResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const expenseApprovalsInfiniteQueryOptions = (params: ExpenseApprovalSearch) =>
  infiniteQueryOptions({
    queryKey: billingQueryKeys.expenseApprovals(params),
    queryFn: async ({ pageParam }): Promise<ExpenseHeaderResponse> => {
      const res = await apiClient.get(API.billing.expenseApproval, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_BILLING_PAGE_SIZE },
      });
      return expenseHeaderResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const expenseApprovalQueryOptions = (expenseId: number | null) =>
  queryOptions({
    queryKey: billingQueryKeys.expenseApproval(expenseId ?? 0),
    queryFn: async (): Promise<ExpenseApprovalDetail> => {
      const res = await apiClient.get(API.billing.expenseApprovalDetail(expenseId!));
      return expenseApprovalDetailSchema.parse(res.data);
    },
    enabled: expenseId !== null,
    staleTime: STALE_TIME,
  });

export const expenseTypeQueryOptions = () =>
  queryOptions({
    queryKey: billingQueryKeys.expenseTypes(),
    queryFn: async (): Promise<ExpenseType[]> => {
      const res = await apiClient.get(API.billing.expenseTypes);
      return expenseTypeListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export async function approveDcr(recordId: number, payload: ApproveDcrPayload): Promise<void> {
  await apiClient.patch(API.billing.approveDcr(recordId), payload);
}

export async function approveDer(detailId: number, payload: ApproveDerPayload): Promise<void> {
  await apiClient.patch(API.billing.approveDer(detailId), payload);
}

export async function approveExpense(
  expenseId: number,
  payload: ApproveExpensePayload,
): Promise<void> {
  await apiClient.patch(API.billing.approveExpense(expenseId), payload);
}

export const allCostInfiniteQueryOptions = (params: AllCostSearch) =>
  infiniteQueryOptions({
    queryKey: billingQueryKeys.allCost(params),
    queryFn: async ({ pageParam }): Promise<AllCostResponse> => {
      const res = await apiClient.get(API.billing.allCost, {
        params: { ...params, page: pageParam, pageSize: DEFAULT_BILLING_PAGE_SIZE },
      });
      return allCostResponseSchema.parse(res.data);
    },
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export async function amendDcr(recordId: number, payload: AmendDcrPayload): Promise<void> {
  await apiClient.patch(API.billing.amendDcr(recordId), payload);
}

export async function amendDer(detailId: number, payload: AmendDerPayload): Promise<void> {
  await apiClient.patch(API.billing.amendDer(detailId), payload);
}

export async function createWebExpense(payload: CreateWebExpensePayload): Promise<void> {
  await apiClient.post(API.billing.webExpenses, payload);
}
